import { InjectionToken, type Provider } from '@angular/core';
import { DEFAULT_STAFF_PROVIDER } from './staff-oidc-auth-url.util';

/**
 * staff 인가(`spinoff-platform-staff-authz`)를 도입한 앱이 `me` 응답에서 권한을 읽는 방법.
 * `me` 는 각 앱 BFF 컨트롤러가 가진 앱 소유 응답이라 이 lib 은 모양을 모른다 — 앱이 자기 orval 생성 타입으로
 * 좁혀 읽는 함수를 넘긴다(`isAuthorized` 와 같은 방식). 응답 타입을 이 lib 에 손으로 옮겨 적지 않는다.
 */
export interface StaffOidcAuthzConfig {
  /** 지금 이 scope 를 쓸 수 있는가(표시용 — 서버가 라우트마다 따로 거부한다). */
  hasScope: (me: unknown, scope: string) => boolean;
  canManageStaff: (me: unknown) => boolean;
  /** 본인의 로컬 `Staff.id` — 관리 화면이 목록에서 본인 행을 알아보는 데 쓴다. 목록 API 의 `staffId` 와 같은 값이다. */
  staffId: (me: unknown) => string;
  /**
   * 저장된 초대 상태(`Pending`·`Accepted`·`Rejected`·`Cancelled`·`Expired`) — `Accepted` 가 아니면 서버가 scope 요구를 모두 거부하므로 화면은 초대 수락 화면으로 안내한다.
   * 초대를 도입하지 않은 앱은 생략한다(항상 `Accepted` 로 본다).
   */
  invitationStatus?: (me: unknown) => string;
}

export interface StaffOidcAuthConfig {
  /**
   * `/staff/auth/me` 200 응답을 인증 완료로 볼지 판정한다. 기본값은 항상 `true` —
   * 백엔드 `StaffSessionGuard`가 이미 세션 유효성을 게이트하므로 200 자체가 인증 완료를
   * 의미하는 앱(sms-staff-web)에 맞는 기본값이다. `me` 응답에 `isStaff` 같은 별도 플래그가
   * 있는 앱(commerce-staff-web)은 이 훅으로 좁혀서 판정한다.
   */
  isAuthorized?: (me: unknown) => boolean;
  /**
   * return 경로가 비어있거나 `/`일 때 대체할 경로. 기본값 없음(계산된 경로를 그대로 쓴다).
   * sms-staff-web처럼 `/`가 실제로는 없는 경로일 때만 지정한다.
   */
  returnPathFallback?: string;
  /**
   * 로그인에 쓸 provider 슬러그 — `<api>/staff/auth/sso/<provider>/authorize-redirect`.
   * 기본값 `'schoolbell-e'`.
   */
  provider?: string;
  /** 인가 도입 앱만 준다. 없으면 `StaffAuthzService` 는 모든 scope 를 허용한다(비활성하지 않고 서버 판단에 맡김). */
  authz?: StaffOidcAuthzConfig;
  /**
   * 앱이 **자체 로그인 화면**을 가진 경우(SSO 가 아니라 전화번호+비밀번호 등) 로그인이 필요할 때 부르는 함수 — 기본의 SSO `authorize-redirect` 이동 대신 이것으로 이동한다.
   * 이 앱은 로그인 응답의 access token 을 `StaffOidcAuthService.adoptSession` 으로 넘겨 이후 refresh·Bearer·로그아웃을 이 lib 에 맡긴다. 생략하면 SSO.
   * 주입 컨텍스트에서 호출되므로 함수 안에서 `inject(Router)` 를 쓸 수 있다.
   */
  signIn?: (returnPath: string) => void;
  /**
   * 호출부가 401 을 스스로 처리하는 요청인지(예: 에러 메시지를 직접 보여 주는 화면의 호출) — `true` 이면 `staffBearerInterceptor` 가 refresh 에 실패해도 로그인으로 이동시키지 않는다
   * (이동과 호출부의 처리가 화면을 다투지 않게). `/staff/auth/*` 는 기존대로 항상 이 대상이다.
   */
  handlesUnauthorized?: (url: string) => boolean;
}

export interface ResolvedStaffOidcAuthConfig {
  isAuthorized: (me: unknown) => boolean;
  returnPathFallback: string;
  provider: string;
  authz: StaffOidcAuthzConfig | null;
  signIn: ((returnPath: string) => void) | null;
  handlesUnauthorized: (url: string) => boolean;
}

export const STAFF_OIDC_AUTH_CONFIG =
  new InjectionToken<ResolvedStaffOidcAuthConfig>('STAFF_OIDC_AUTH_CONFIG');

export function provideStaffOidcAuth(
  config: StaffOidcAuthConfig = {},
): Provider {
  const resolved: ResolvedStaffOidcAuthConfig = {
    isAuthorized: config.isAuthorized ?? (() => true),
    returnPathFallback: config.returnPathFallback ?? '',
    provider: config.provider ?? DEFAULT_STAFF_PROVIDER,
    authz: config.authz ?? null,
    signIn: config.signIn ?? null,
    handlesUnauthorized: config.handlesUnauthorized ?? (() => false),
  };
  return { provide: STAFF_OIDC_AUTH_CONFIG, useValue: resolved };
}
